import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { listBentukPendidikan } from "@/constants"
import api from "@/lib/axios"
import { JurusanSekolah } from "@/types"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"
import { FaPlus } from "react-icons/fa6"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import { useMutation, useQueryClient } from "@tanstack/react-query"

const postData = async (newData: JurusanSekolah) => {
  const response = await api.post("/master/jurusan-sekolah/store", newData)
  return response.data
}

const patchData = async (newData: JurusanSekolah) => {
  const response = await api.patch("/master/jurusan-sekolah/update", newData)
  return response.data
}

const deleteData = async (deletedData: { id_ref_jurusan_sekolah: number }) => {
  const response = await api({
    url: "/master/jurusan-sekolah/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: JurusanSekolah | null
  closeModal: () => void
}) {
  const [form, setForm] = useState<JurusanSekolah>({
    id_ref_jurusan_sekolah: selectedRow?.id_ref_jurusan_sekolah || 0,
    kode_jurusan_snpmb: selectedRow?.kode_jurusan_snpmb || "",
    nama_jurusan_sekolah: selectedRow?.nama_jurusan_sekolah || "",
    bentuk_pendidikan: selectedRow?.bentuk_pendidikan || [],
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listJurusanSekolah"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data NominalUkt"
          description={`Apakah anda yakin akan menghapus data Jurusan Sekolah "${selectedRow?.nama_jurusan_sekolah}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={
          isPost ? "Insert Data Jurusan Sekolah" : "Update Data Jurusan Sekolah"
        }
      />

      <div>
        <Label>Nama Jurusan Sekolah</Label>
        <Input
          required
          maxLength={4}
          placeholder="Ketik nama jurusan sekolah..."
          value={form.nama_jurusan_sekolah}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              nama_jurusan_sekolah: event.target.value,
            }))
          }
        />
      </div>
      <div>
        <Label>Bentuk Pendidikan</Label>
        <div className="mt-2 flex flex-wrap gap-5">
          {listBentukPendidikan.map((value, index) => {
            // Pastikan bentuk_pendidikan adalah array string
            const isChecked =
              Array.isArray(form.bentuk_pendidikan) &&
              form.bentuk_pendidikan.includes(value)

            // Tambahkan tipe eksplisit untuk `checked`
            const handleCheckedChange = (checked: boolean) => {
              if (checked) {
                setForm((prevForm) => ({
                  ...prevForm,
                  bentuk_pendidikan: [
                    ...(Array.isArray(prevForm.bentuk_pendidikan)
                      ? prevForm.bentuk_pendidikan
                      : []),
                    value,
                  ],
                }))
              } else {
                setForm((prevForm) => ({
                  ...prevForm,
                  bentuk_pendidikan: Array.isArray(prevForm.bentuk_pendidikan)
                    ? prevForm.bentuk_pendidikan.filter(
                        (item: string) => item !== value
                      )
                    : [],
                }))
              }
            }

            return (
              <div key={index} className="flex gap-3">
                <Checkbox
                  checked={isChecked}
                  onCheckedChange={handleCheckedChange}
                />
                <Label>{value}</Label>
              </div>
            )
          })}
        </div>
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
